import { apiClient } from '../config';
import { endpoints } from '../endpoints';

/**
 * Service for publication-related API calls
 */
export const publicationService = {
  /**
   * Get all publications
   * @param {Object} params - Query parameters
   * @returns {Promise<Array>} - List of publications
   */
  getAllPublications: async (params = {}) => {
    const response = await apiClient.get('/publications', { params });
    return response.data;
  },

  /**
   * Get publication by ID
   * @param {string} id - Publication ID
   * @returns {Promise<Object>} - Publication details
   */
  getPublicationById: async (id) => {
    const response = await apiClient.get(`/publications/${id}`);
    return response.data;
  },

  /**
   * Get publications by issue
   * @param {string} issueId - Issue ID
   * @param {Object} params - Query parameters
   * @returns {Promise<Array>} - List of publications in the issue
   */
  getPublicationsByIssue: async (issueId, params = {}) => {
    const response = await apiClient.get(`/publications/issue/${issueId}`, { params });
    return response.data;
  },

  /**
   * Get publications by author
   * @param {string} authorId - Author ID
   * @param {Object} params - Query parameters
   * @returns {Promise<Array>} - List of author's publications
   */
  getAuthorPublications: async (authorId, params = {}) => {
    const response = await apiClient.get(`/publications/author/${authorId}`, { params });
    return response.data;
  },

  /**
   * Get publications by journal
   * @param {string} journalId - Journal ID
   * @param {Object} params - Query parameters
   * @returns {Promise<Array>} - List of publications in the journal
   */
  getPublicationsByJournal: async (journalId, params = {}) => {
    const response = await apiClient.get(`/publications/journal/${journalId}`, { params });
    return response.data;
  },

  /**
   * Get publications by volume
   * @param {string} volumeId - Volume ID
   * @param {Object} params - Query parameters
   * @returns {Promise<Array>} - List of publications in the volume
   */
  getPublicationsByVolume: async (volumeId, params = {}) => {
    const response = await apiClient.get(`/publications/volume/${volumeId}`, { params });
    return response.data;
  },

  /**
   * Create a new publication (Chief Editor only)
   * @param {Object} publicationData - Publication data
   * @returns {Promise<Object>} - Created publication
   */
  createPublication: async (publicationData) => {
    const response = await apiClient.post('/publications', publicationData);
    return response.data;
  },

  /**
   * Update publication status (Chief Editor only)
   * @param {string} publicationId - Publication ID
   * @param {string} status - New status
   * @returns {Promise<Object>} - Updated publication
   */
  updatePublicationStatus: async (publicationId, status) => {
    const response = await apiClient.patch(`/publications/${publicationId}/status`, { status });
    return response.data;
  },

  /**
   * Get current user's publications
   * @param {Object} params - Query parameters
   * @returns {Promise<Array>} - List of user's publications
   */
  getUserPublications: async (params = {}) => {
    const response = await apiClient.get('/publications/user/publications', { params });
    return response.data;
  },

  /**
   * Update publication content (Chief Editor only)
   * @param {string} publicationId - Publication ID
   * @param {Object} publicationData - Updated publication data
   * @param {File} file - Optional PDF file to upload
   * @returns {Promise<Object>} - Updated publication
   */
  updatePublication: async (publicationId, publicationData, file) => {
    // If a file is provided, use FormData to send both data and file
    if (file) {
      const formData = new FormData();

      // Append the file
      formData.append('file', file);

      // Append publication data as JSON
      Object.keys(publicationData).forEach(key => {
        if (publicationData[key] !== undefined) {
          if (typeof publicationData[key] === 'object' && !(publicationData[key] instanceof File)) {
            formData.append(key, JSON.stringify(publicationData[key]));
          } else {
            formData.append(key, publicationData[key]);
          }
        }
      });

      const response = await apiClient.patch(`/publications/${publicationId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      return response.data;
    } else {
      // If no file, use regular JSON request
      const response = await apiClient.patch(`/publications/${publicationId}`, publicationData);
      return response.data;
    }
  },

  /**
   * Delete publication attachment (Chief Editor only)
   * @param {string} attachmentId - Attachment ID
   * @returns {Promise<void>}
   */
  deleteAttachment: async (attachmentId) => {
    await apiClient.delete(`/attachments/publication/${attachmentId}`);
  },

  /**
   * Delete author from publication (Admin only)
   * @param {string} publicationId - Publication ID
   * @param {string} authorId - Author ID to remove
   * @returns {Promise<void>}
   */
  deleteAuthor: async (publicationId, authorId) => {
    await apiClient.delete(`/publications/${publicationId}/authors/${authorId}`);
  },

  /**
   * Delete publication and all associated data
   * @param {string} publicationId - Publication ID
   * @param {boolean} deleteManuscript - Whether to also delete the linked manuscript
   * @returns {Promise<void>}
   */
  deletePublication: async (publicationId, deleteManuscript = false) => {
    await apiClient.delete(endpoints.publications.delete(publicationId), {
      params: { deleteManuscript }
    });
  },

  /**
   * Get spotlight publications
   * @param {Object} params - Query parameters (e.g., limit)
   * @returns {Promise<Array>} - List of spotlight publications
   */
  getSpotlightPublications: async (params = {}) => {
    const response = await apiClient.get('/publications/spotlight', { params });
    return response.data;
  },

  /**
   * Toggle publication spotlight status (Chief Editor only)
   * @param {string} publicationId - Publication ID
   * @param {boolean} isSpotlight - Whether to mark as spotlight
   * @returns {Promise<Object>} - Updated publication
   */
  togglePublicationSpotlight: async (publicationId, isSpotlight) => {
    const response = await apiClient.patch(`/publications/${publicationId}/spotlight`, { isSpotlight });
    return response.data;
  },

  /**
   * Record a view event for a publication
   * @param {string} publicationId - Publication ID
   * @returns {Promise<void>}
   */
  recordViewEvent: async (publicationId) => {
    try {
      await apiClient.post(`/publication-metrics/${publicationId}/view`);
    } catch (error) {
      console.error('Error recording view event:', error);
      // Don't throw the error to avoid disrupting the user experience
    }
  },

  /**
   * Record a download event for a publication
   * @param {string} publicationId - Publication ID
   * @returns {Promise<void>}
   */
  recordDownloadEvent: async (publicationId) => {
    try {
      await apiClient.post(`/publication-metrics/${publicationId}/download`);
    } catch (error) {
      console.error('Error recording download event:', error);
      // Don't throw the error to avoid disrupting the user experience
    }
  },

  /**
   * Get the download URL for a publication attachment
   * @param {string} attachmentId - Attachment ID
   * @returns {string} - URL for downloading the attachment
   */
  getPublicationAttachmentDownloadUrl: (attachmentId) => {
    return endpoints.attachments.downloadPublicationFile(attachmentId);
  },

  /**
   * Get all available categories
   * @returns {Promise<Array<string>>} - List of categories
   */
  getCategories: async () => {
    const response = await apiClient.get('/publications/categories');
    return response.data;
  },

  /**
   * Get all available publication years
   * @returns {Promise<Array<number>>} - List of years
   */
  getPublicationYears: async () => {
    const response = await apiClient.get('/publications/years');
    return response.data;
  },

  /**
   * Get publication by ID for editing (with all fields)
   * @param {string} id - Publication ID
   * @returns {Promise<Object>} - Publication details with all fields for editing
   */
  getPublicationForEdit: async (id) => {
    const response = await apiClient.get(`/publications/get-for-edit/${id}`);
    return response.data;
  }
};

export default publicationService;
